import React, { useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { createVendor, editVendor, getVendors } from '../../apollo'
import {
  Box, Typography, TextField, Button, Alert, CircularProgress
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'

const CREATE_VENDOR = gql`${createVendor}`
const EDIT_VENDOR = gql`${editVendor}`
const GET_VENDORS = gql`${getVendors}`

function Vendor(props) {
  const isEdit = !!props.vendor
  const mutation = isEdit ? EDIT_VENDOR : CREATE_VENDOR

  const [name, setName] = useState(props.vendor?.name || '')
  const [email, setEmail] = useState(props.vendor?.email || '')
  const [nameError, setNameError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_VENDORS }],
    onCompleted: () => {
      setSuccess(isEdit ? 'Merchant updated successfully!' : 'Account created! Temporary password sent to their email.')
      setError('')
      setTimeout(() => {
        if (typeof props.onClose === 'function') props.onClose()
      }, 2000)
    },
    onError: err => {
      setError(err.graphQLErrors?.[0]?.message || err.message || 'Something went wrong')
      setSuccess('')
    }
  })

  const handleSubmit = () => {
    let valid = true
    if (!name.trim()) { setNameError('Full name is required'); valid = false } else setNameError('')
    if (!email.trim()) { setEmailError('Email address is required'); valid = false }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError('Enter a valid email'); valid = false }
    else setEmailError('')
    if (!valid) return

    mutate({
      variables: {
        vendorInput: {
          _id: isEdit ? props.vendor._id : '',
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password: isEdit ? '' : Math.random().toString(36).slice(-8)
        }
      }
    })
  }

  return (
    <Box>
      {/* Header note (create only) */}
      {!isEdit && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, mb: 2.5, borderRadius: '12px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <BusinessIcon sx={{ color: '#16a34a', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.82rem', color: '#15803d', lineHeight: 1.5 }}>
            A temporary password will be generated and emailed to the merchant automatically.
          </Typography>
        </Box>
      )}

      {/* Full Name */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.6 }}>Full Name</Typography>
        <TextField
          fullWidth size="small"
          placeholder="e.g. John Smith"
          value={name}
          onChange={e => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError}
          autoFocus
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
      </Box>

      {/* Email */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.6 }}>Email Address</Typography>
        <TextField
          fullWidth size="small" type="email"
          placeholder="e.g. merchant@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}

      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button
          onClick={() => props.onClose?.()}
          sx={{ borderRadius: '10px', textTransform: 'none', color: '#64748b', fontWeight: 600, px: 2.5 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : isEdit ? <EditIcon /> : <AddIcon />}
          onClick={handleSubmit}
          disabled={loading}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}>
          {loading ? 'Saving...' : isEdit ? 'Update Merchant' : 'Create Account'}
        </Button>
      </Box>
    </Box>
  )
}

export default withTranslation()(Vendor)